import React, { useState } from 'react';
import { graphql } from 'gatsby';
import CtaLink from '../Global/CtaLink';
import Modal from 'react-modal';
//@ts-ignore
import * as styles from './CtaBar.module.css';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import CloseButton from '../Global/CloseButton';
import RichText from '../Global/RichText';
import {
    KontentStringValue,
    CTAConfiguration,
    KontentRadioMultiChoice,
    BrandColor,
    KontentRichText,
    CtaTsType,
} from '../../../types/KontentProps';
import { getColor, getMultiChoice } from '../Global/DataUtils';
import FormContainerWImage from './FormContainerWImage';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import AnchorLink from '../Global/AnchorLink';
import { isRichTextFieldBlank } from '../../global/utility';

interface Props extends CTAConfiguration {
    kontentItemId?: string;
    content_panel__content_panel_color?: BrandColor;
    content_panel__content_panel_font_color?: BrandColor;
    copy: KontentRichText;
    layout: {
        value: [
            {
                name: string;
            }
        ];
    };
    ctas: {
        value: CtaTsType[];
    };
    type_of_cta?: KontentRadioMultiChoice<'page_link' | 'form' | undefined>;
    form_cta_label?: KontentStringValue;
    form_component?: any;
}

const CtaBar: React.FC<Props> = ({
    kontentItemId,
    content_panel__content_panel_color,
    content_panel__content_panel_font_color,
    ctas,
    copy,
    layout,
    type_of_cta,
    form_cta_label,
    form_component,
    ...props
}) => {
    const [showForm, setShowForm] = useState(false);
    const form_link = form_component?.value[0]?.elements?.form_link;
    const cta = ctas.value;
    const action = layout.value[0]?.name;
    const breakpoints = useBreakpoint();
    const isMobile = !breakpoints.sm;
    const contentPanelColor = getColor(content_panel__content_panel_color);
    const contentPanelFontColor = getColor(
        content_panel__content_panel_font_color
    );
    const useFormLink = getMultiChoice(type_of_cta) === 'form';

    const textColor = contentPanelFontColor || 'var(--color-dark-gray)';
    const backgroundColor = contentPanelColor ? contentPanelColor : undefined;
    const visualStyle =
        contentPanelFontColor === '#FFFFFF' ? 'outlineLight' : 'outlineDark';

    const buttonComponent = () => {
        return (
            <div className={styles.ctaContainer}>
                {useFormLink ? (
                    <button
                        onClick={() => setShowForm(true)}
                        type="button"
                        style={{
                            color: textColor,
                            borderColor: textColor,
                            background: backgroundColor,
                        }}
                        data-kontent-element-codename="form_cta_label"
                    >
                        {form_cta_label?.value}
                    </button>
                ) : (
                    cta.map((cta, index) => {
                        return !cta.elements.cta_location?.value?.length &&
                            cta.elements?.cta_location_anchor_point ? (
                            <AnchorLink
                                key={index}
                                targetId={
                                    cta.elements.cta_location_anchor_point
                                        ?.value
                                }
                                color={textColor}
                                visualStyle={visualStyle}
                            >
                                {cta.elements.cta_label.value}
                            </AnchorLink>
                        ) : (
                            <CtaLink
                                key={index}
                                href={BuildLinkHref(
                                    cta.elements.cta_location,
                                    cta.elements.cta_location_anchor_point
                                )}
                                target={
                                    getMultiChoice(
                                        cta.elements.cta_window_action
                                    ) === 'new_window'
                                        ? '_blank'
                                        : undefined
                                }
                                color={textColor}
                                visualStyle={visualStyle}
                                elementCodename="cta_label"
                            >
                                {cta.elements.cta_label.value}
                            </CtaLink>
                        );
                    })
                )}
                {showForm && form_link?.value && (
                    <div>
                        <Modal
                            contentLabel="Form"
                            isOpen={showForm}
                            onRequestClose={() => setShowForm(false)}
                            shouldCloseOnOverlayClick={true}
                            className={`${styles.modalContent} ${
                                isMobile
                                    ? styles.mobilemodalContent
                                    : styles.desktopmodalContent
                            }`}
                            overlayClassName={styles.overlayJotForm}
                        >
                            <div>
                                <div className={styles.closeButton}>
                                    <CloseButton
                                        visualStyle="orange"
                                        onClick={() => setShowForm(false)}
                                    />
                                </div>
                                <FormContainerWImage
                                    {...form_component?.value[0]?.elements}
                                />
                            </div>
                        </Modal>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            style={{
                backgroundColor: backgroundColor,
                color: textColor,
            }}
            className={`${styles.container} ${
                !isRichTextFieldBlank(copy.value) ? styles.extraPadding : ''
            }`}
            data-kontent-item-id={kontentItemId}
        >
            {copy?.value != '<p><br></p>' && (
                <div className={styles.content}>
                    {RichText(copy, false, undefined, undefined, 'copy')}
                </div>
            )}
            {buttonComponent()}
        </div>
    );
};

export default CtaBar;

export const fragmentCtaBar = graphql`
    fragment CtaBar on kontent_item_component___cta_bar {
        elements {
            ctas {
                value {
                    ...kontentCta
                }
            }
            layout {
                value {
                    name
                }
            }
            copy {
                ...kontentRichText
            }
            content_panel__content_panel_font_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            content_panel__content_panel_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            type_of_cta {
                value {
                    codename
                }
            }
            form_cta_label {
                value
            }
            form_component {
                value {
                    ... on kontent_item_component___form_container_w__image {
                        id
                        elements {
                            desktop_image {
                                ...kontentImage
                            }
                            desktop_image_position {
                                value {
                                    codename
                                }
                            }
                            mobile_image_position {
                                value {
                                    codename
                                }
                            }
                            mobile_image {
                                ...kontentImage
                            }
                            form_headline {
                                value
                            }
                            form_description {
                                value
                            }
                            form_link {
                                value
                            }
                            anchor {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
